// Emits the "Minisearch Results Report" — one analytics event per settled
// content search, describing what the user was shown and how every shadow
// profile would have answered the same query.
//
// There is no gold standard any more. The displayed profile is production, and
// each shadow profile is described by how far it AGREES with it: which ids both
// returned, which only one returned, and whether the shared ids are in the same
// order. Agreement says where a candidate differs, not whether it is better —
// which one is better comes from what users do with the results, reported per
// action by searchSession.js as "Minisearch Results Outcome" and joined to this
// event on `searchId`.
//
// Non-content registries (navigation / external-help / quick-actions) are
// recorded as what they returned; they have no shadow lanes.

import { getDocumentType } from '../../utils/search/getDocumentType'
import { localProviderId } from './presentationEngine'
import { searchDebugLog } from './searchDebugLog'
import analytics from '../../utils/cdpAnalytics'

export const RESULTS_REPORT_EVENT = 'Minisearch Results Report'

// The minisearch package's `exports` map hides its package.json, so the
// version is pinned here and a test asserts it matches the installed package.
// A library upgrade can change matching; rows either side must be separable.
export const MINISEARCH_VERSION = '6.3.0'

const CONTENT_INDEXES = ['repos', 'folders', 'sets_questions']
const NON_CONTENT_SOURCES = ['navigation', 'external-help', 'quick-actions']

// How many shadow lanes get their own flat columns. Slots are positional, so a
// renamed or replaced profile lands in the same columns rather than new ones.
const SHADOW_COLUMN_SLOTS = 2

// The ordered displayed list is the replay checksum: rebuild the index, re-run
// the profile, and the output must match it. Each shadow lane carries its own
// ordered list too: a lane that shows the same documents in a different order
// has no difference lists at all, so without it the order a candidate would
// have shown -- the thing a side-by-side review compares -- is not recorded
// anywhere. Capped because a sectionHome query can return 200 hits per index.
export const REPORT_LIST_CAP = 20

// Per-lane difference lists are for eyeballing a disagreement, not for
// reconstructing it; the counts carry the full story.
const DIFF_LIST_CAP = 10

// cdpDumpPlugin silently drops any event whose whole envelope exceeds 32 KiB
// (`maxEventBytes`). The envelope adds page context on top of these props, so
// the props are held well below that.
export const MAX_REPORT_BYTES = 28000

// Round to 4 decimals; pass through non-finite/non-number values unchanged.
function round(n) {
  if (typeof n !== 'number' || !Number.isFinite(n)) return n
  return Number(n.toFixed(4))
}

// UTF-8 byte length. The payload limit is in bytes, and question text in
// non-Latin scripts is several bytes per character.
function byteLength(text) {
  let bytes = 0
  for (let i = 0; i < text.length; i += 1) {
    const code = text.charCodeAt(i)
    if (code < 0x80) bytes += 1
    else if (code < 0x800) bytes += 2
    else if (code >= 0xd800 && code <= 0xdbff) {
      // A surrogate pair is one 4-byte code point.
      bytes += 4
      i += 1
    } else bytes += 3
  }
  return bytes
}

// Pearson correlation of two equal-length numeric arrays. Returns null when
// undefined (fewer than 2 points, or zero variance on either side).
function pearson(xs, ys) {
  const n = xs.length
  if (n < 2) return null
  const mean = (arr) => arr.reduce((acc, v) => acc + v, 0) / arr.length
  const mx = mean(xs)
  const my = mean(ys)
  let num = 0
  let dx = 0
  let dy = 0
  for (let i = 0; i < n; i += 1) {
    const a = xs[i] - mx
    const b = ys[i] - my
    num += a * b
    dx += a * a
    dy += b * b
  }
  if (dx === 0 || dy === 0) return null
  return num / Math.sqrt(dx * dy)
}

// Spearman rank correlation over the objectIDs shared by two ordered id lists,
// approximated as the Pearson correlation of their positions. Returns null when
// fewer than 2 ids are shared.
function rankCorrelation(orderA, orderB) {
  const idxB = new Map(orderB.map((id, i) => [id, i]))
  const shared = orderA.filter((id) => idxB.has(id))
  if (shared.length < 2) return null
  const idxA = new Map(orderA.map((id, i) => [id, i]))
  const xs = shared.map((id) => idxA.get(id))
  const ys = shared.map((id) => idxB.get(id))
  return pearson(xs, ys)
}

function describeHit(hit) {
  return { objectID: hit.objectID, documentType: getDocumentType(hit) }
}

function objectIds(hits) {
  return (Array.isArray(hits) ? hits : []).map((h) => h.objectID)
}

function indexSlot(indexResults, indexName) {
  const slot = indexResults && indexResults[indexName]
  return slot || { hits: [], hitCount: 0, hasMore: false }
}

function slotHits(slot) {
  return Array.isArray(slot.hits) ? slot.hits : []
}

// How one shadow lane's hits for an index compare with the displayed hits.
// Both lists are capped at the same surface limit, so this compares what the
// user saw with what they would have seen.
//
//   shared           ids both returned
//   displayOnly      ids only production returned (the candidate would drop them)
//   shadowOnly       ids only the candidate returned (it would add them)
//   rankCorrelation  ordering agreement over the shared ids; +1 same order,
//                    −1 reversed, null when fewer than two are shared
export function compareLanes(displayHits, shadowHits) {
  const displayIds = objectIds(displayHits)
  const shadowIds = objectIds(shadowHits)
  const displaySet = new Set(displayIds)
  const shadowSet = new Set(shadowIds)
  const displayOnlyHits = displayHits.filter((h) => !shadowSet.has(h.objectID))
  const shadowOnlyHits = shadowHits.filter((h) => !displaySet.has(h.objectID))
  const rho = rankCorrelation(displayIds, shadowIds)
  return {
    hitCount: shadowIds.length,
    shared: shadowIds.length - shadowOnlyHits.length,
    displayOnly: displayOnlyHits.length,
    shadowOnly: shadowOnlyHits.length,
    rankCorrelation: rho == null ? null : round(rho),
    displayOnlyHits: displayOnlyHits.slice(0, DIFF_LIST_CAP).map(describeHit),
    shadowOnlyHits: shadowOnlyHits.slice(0, DIFF_LIST_CAP).map(describeHit),
  }
}

// [objectID, score] for the first REPORT_LIST_CAP hits, in order.
function orderedList(hits) {
  return hits.slice(0, REPORT_LIST_CAP).map((h) => [
    h.objectID,
    typeof h.__minisearchScore === 'number' ? round(h.__minisearchScore) : null,
  ])
}

function buildDisplayReport(displayIndexResults) {
  const out = {}
  CONTENT_INDEXES.forEach((indexName) => {
    const slot = indexSlot(displayIndexResults, indexName)
    const hits = slotHits(slot)
    out[indexName] = {
      shown: hits.length,
      hitCount: typeof slot.hitCount === 'number' ? slot.hitCount : hits.length,
      hasMore: Boolean(slot.hasMore),
      hits: orderedList(hits),
    }
  })
  return out
}

// Weighted by how many hits production showed in each index, so an index that
// showed nothing cannot drag the summary around.
function summariseLane(perIndex, displayReport) {
  let shared = 0
  let displayOnly = 0
  let shadowOnly = 0
  let hitCount = 0
  let rho = 0
  let rhoWeight = 0
  CONTENT_INDEXES.forEach((indexName) => {
    const metric = perIndex[indexName]
    shared += metric.shared
    displayOnly += metric.displayOnly
    shadowOnly += metric.shadowOnly
    hitCount += metric.hitCount
    const weight = displayReport[indexName].shown
    if (metric.rankCorrelation != null && weight > 0) {
      rho += metric.rankCorrelation * weight
      rhoWeight += weight
    }
  })
  return {
    hitCount,
    shared,
    displayOnly,
    shadowOnly,
    rankCorrelation: rhoWeight ? round(rho / rhoWeight) : null,
  }
}

function buildShadowReport(displayIndexResults, shadows, displayReport) {
  const out = {}
  shadows.forEach(({ profile, indexResults }) => {
    const perIndex = {}
    CONTENT_INDEXES.forEach((indexName) => {
      const shadowHits = slotHits(indexSlot(indexResults, indexName))
      perIndex[indexName] = {
        ...compareLanes(slotHits(indexSlot(displayIndexResults, indexName)), shadowHits),
        hits: orderedList(shadowHits),
      }
    })
    out[profile] = { overall: summariseLane(perIndex, displayReport), indices: perIndex }
  })
  return out
}

function getFlatHits(additiveResults, providerId) {
  if (!Array.isArray(additiveResults)) return []
  const block = additiveResults.find((r) => r.providerId === providerId)
  return (block && Array.isArray(block.hits)) ? block.hits : []
}

// Recorded as what each registry returned — enough to spot a registry that has
// silently stopped matching anything.
function buildNonContentReport(additiveResults) {
  const out = {}
  NON_CONTENT_SOURCES.forEach((base) => {
    const hits = getFlatHits(additiveResults, localProviderId(base))
    out[base] = {
      hits: hits.length,
      results: hits.map((h) => ({ objectID: h.objectID, label: h.label })),
    }
  })
  return out
}

// ─── Coverage ──────────────────────────────────────────────────────────────
//
// Two numbers per index: what the API says the account holds, and what
// MiniSearch actually indexed. Index sizes alone cannot tell a small library
// from a half-loaded one.
//
// Which API collections feed each search index. `sets_questions` is one index
// built from two independently loaded collections, so it is only complete when
// both of them are.
const INDEX_COLLECTIONS = {
  repos: ['repos'],
  folders: ['folders'],
  // eslint-disable-next-line camelcase
  sets_questions: ['sets', 'questions'],
}

function sumExpected(loadStatus, collections) {
  const known = collections
    .map((name) => (loadStatus[name] ? loadStatus[name].expected : null))
    .filter((expected) => typeof expected === 'number')
  if (known.length !== collections.length) return null
  return known.reduce((total, expected) => total + expected, 0)
}

// `>=` rather than `===` because browsing a repo loads content beyond the
// initial library fetch, so only indexed < expected is evidence of a problem.
//
// Returns null whenever the question cannot be answered yet — a collection that
// has not reported is not a collection that failed, and conflating the two
// would make every early-session query look like a coverage problem.
function indexComplete(indexed, expected, loadsOk) {
  if (loadsOk === false) return false
  if (loadsOk === null || indexed === null || expected === null) return null
  return indexed >= expected
}

// Strips the per-request detail the reducer keeps for its own bookkeeping.
function reportableLoads(loadStatus) {
  return Object.keys(loadStatus).reduce((acc, name) => {
    const {
      ok, failed, expected, at,
    } = loadStatus[name]
    return {
      ...acc,
      [name]: {
        ok, failed, expected, at,
      },
    }
  }, {})
}

export function buildCoverage(indexSizes, loadStatus) {
  const loads = loadStatus || {}
  const local = {}
  CONTENT_INDEXES.forEach((indexName) => {
    const collections = INDEX_COLLECTIONS[indexName]
    const indexed = (indexSizes && typeof indexSizes[indexName] === 'number') ?
      indexSizes[indexName] :
      null
    const expected = sumExpected(loads, collections)
    // null when a contributing collection has not reported yet, which is not
    // the same as it having failed
    const reported = collections.filter((name) => loads[name] && loads[name].ok !== null)
    let loadsOk = null
    if (reported.length === collections.length) {
      loadsOk = collections.every((name) => loads[name].ok === true)
    }
    local[indexName] = { indexed, expected, complete: indexComplete(indexed, expected, loadsOk) }
  })

  const perIndexComplete = CONTENT_INDEXES.map((indexName) => local[indexName].complete)
  let complete = null
  if (perIndexComplete.some((value) => value === false)) complete = false
  else if (perIndexComplete.every((value) => value === true)) complete = true

  return { complete, local, loads: reportableLoads(loads) }
}

/**
 * Build the results report for one settled query.
 *
 * @param {Object} input
 * @param {string} input.searchId           Joins this row to its outcome events
 * @param {string} input.query
 * @param {string} input.surface            'topNav' | 'searchResults' | 'sectionHome'
 * @param {Object} input.limits             Requested hitsPerPage and per-index cap
 * @param {string} input.displayProfile     The profile the user saw
 * @param {Object} input.displayIndexResults
 * @param {Array<{profile: string, indexResults: Object}>} input.shadows
 * @param {Object} input.indexSizes         Per-index document counts
 * @param {Array}  input.additiveResults    Non-content registry results
 * @param {Object} input.loadStatus         state.libraryLoadStatus
 * @returns {Object}
 */
export function buildResultsReport({
  searchId,
  query,
  surface,
  limits,
  displayProfile,
  displayIndexResults,
  shadows,
  indexSizes,
  additiveResults,
  loadStatus,
}) {
  const display = buildDisplayReport(displayIndexResults)
  const shadowLanes = Array.isArray(shadows) ? shadows : []
  return {
    searchId,
    query,
    surface: surface || null,
    limits: limits || null,
    minisearchVersion: MINISEARCH_VERSION,
    displayProfile,
    shadowProfiles: shadowLanes.map((s) => s.profile),
    indexSizes: indexSizes || null,
    coverage: buildCoverage(indexSizes, loadStatus),
    display,
    shadows: buildShadowReport(displayIndexResults, shadowLanes, display),
    nonContent: buildNonContentReport(additiveResults),
    truncated: false,
  }
}

// Flatten the headline numbers into top-level scalars so the CDP ingest exposes
// them as queryable columns; nested objects land in one SUPER column.
export function buildResultsReportProps(report) {
  const displayHitCount = CONTENT_INDEXES
    .reduce((acc, indexName) => acc + report.display[indexName].shown, 0)
  const props = {
    searchId: report.searchId,
    query: report.query,
    surface: report.surface,
    displayProfile: report.displayProfile,
    shadowProfiles: report.shadowProfiles.join(','),
    minisearchVersion: report.minisearchVersion,
    displayHitCount,
    zeroResults: displayHitCount === 0,
    // Three-valued: null means not yet known, which must not be filtered as if
    // it were false.
    libraryComplete: report.coverage.complete,
  }
  for (let slot = 0; slot < SHADOW_COLUMN_SLOTS; slot += 1) {
    const profile = report.shadowProfiles[slot] || null
    const lane = profile ? report.shadows[profile].overall : null
    const prefix = `shadow${slot + 1}`
    props[`${prefix}Profile`] = profile
    props[`${prefix}HitCount`] = lane ? lane.hitCount : null
    props[`${prefix}Shared`] = lane ? lane.shared : null
    props[`${prefix}DisplayOnly`] = lane ? lane.displayOnly : null
    props[`${prefix}ShadowOnly`] = lane ? lane.shadowOnly : null
    props[`${prefix}RankCorrelation`] = lane ? lane.rankCorrelation : null
  }
  return { ...props, report }
}

// Shed detail until the event fits, least valuable first: the per-lane
// difference lists, then the ordered lists (displayed and shadow, together, so
// a side-by-side is never left with one side). Counts always survive.
export function fitReportProps(props) {
  const size = (p) => byteLength(JSON.stringify(p))
  if (size(props) <= MAX_REPORT_BYTES) return props

  const report = { ...props.report, truncated: true, shadows: {} }
  Object.keys(props.report.shadows).forEach((profile) => {
    const lane = props.report.shadows[profile]
    const indices = {}
    Object.keys(lane.indices).forEach((indexName) => {
      const { displayOnlyHits, shadowOnlyHits, ...counts } = lane.indices[indexName]
      indices[indexName] = counts
    })
    report.shadows[profile] = { ...lane, indices }
  })
  let fitted = { ...props, report }
  if (size(fitted) <= MAX_REPORT_BYTES) return fitted

  const display = {}
  Object.keys(report.display).forEach((indexName) => {
    const { hits, ...counts } = report.display[indexName]
    display[indexName] = counts
  })
  const shadows = {}
  Object.keys(report.shadows).forEach((profile) => {
    const lane = report.shadows[profile]
    const indices = {}
    Object.keys(lane.indices).forEach((indexName) => {
      const { hits, ...counts } = lane.indices[indexName]
      indices[indexName] = counts
    })
    shadows[profile] = { ...lane, indices }
  })
  fitted = { ...props, report: { ...report, display, shadows } }
  if (size(fitted) <= MAX_REPORT_BYTES) return fitted

  // Last resort: the flat columns alone still answer every headline question.
  const { nonContent, coverage, ...rest } = fitted.report
  return { ...fitted, report: { ...rest, coverage: { complete: coverage.complete } } }
}

/**
 * Build the results report and ship it to the CDP analytics platform.
 * Also logs the JSON to the console outside production.
 *
 * @param {Object} input see buildResultsReport
 */
export function reportSearchResults(input) {
  if (!input || !input.query) return
  const props = fitReportProps(buildResultsReportProps(buildResultsReport(input)))
  analytics.track(RESULTS_REPORT_EVENT, props)
  searchDebugLog(`[searchReporter] ${RESULTS_REPORT_EVENT}`, props)
}
